import { BasketDrawerVersion } from '@common/components/BasketDrawer';
import { FormWrapper, InnerScroller, Modal } from '@common/components/BookingForms';
import { Spinner } from '@common/components/Spinner/Spinner';
import { StickyBarWrapper } from '@common/components/StickyBar';
import { getBookingRoute, getDetailRoute } from '@features/rates/helpers/bookingRoutes.helper';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { some } from '@utils/some';
import moment from 'moment';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useRouter } from 'shared-routing';
import { useInvalidatePackagesAvailability } from '../packages';
import { useRoomExtras } from '../rooms';
import {
  useGetSpaPackage,
  useGetSpaPackageConfigGroups,
  useGetSpaPackageTimeslots,
  useGetSpaSettings,
  useInvalidateSpaPackageConfigGroups,
} from '../spa';
import { useGetSpaPackageExtras } from '../spa/hooks/useGetSpaPackageExtras';
import { useStepNavigation } from './components/StepNavigator/useStepNavigation';
import { STICKY_BAR_ID } from './constants';
import { PackageConfigurationProvider, WizardNavigationProvider } from './contexts';
import {
  DynamicPackagesConfigurationWizardDispatchProps,
  DynamicPackagesConfigurationWizardProps,
  DynamicPackagesConfigurationWizardStoreProps,
} from './DynamicPackagesConfigurationWizard.types';
import { FormSteps } from './FormSteps/FormSteps';
import { Header } from './FormSteps/FormSteps.styles';
import { usePrefetchMultiGuestAvailability } from './FormSteps/hooks/usePrefetchMultiGuestAvailability';
import {
  useRestoredLanguageChangeSnapshot,
  useSaveSnapshotOnLanguageChange,
} from './hooks/useLanguageChangeSnapshot';
import { addToBasket } from './helpers/addToBasket';
import { formatDateISO, formatDateRangeISO } from './helpers/dateFormatters';
import { getExperienceSteps } from './helpers/getExperienceSteps';
import { foldSelectionIntoExperienceStep } from './helpers/foldSelectionIntoExperienceStep';
import { getTablesSteps } from './helpers/getTablesSteps';
import { heldSpaTimesFromBasket } from './helpers/heldSpaTimes';
import { mapLoggedInUserToGuest } from './helpers/mapLoggedInUserToGuest';
import { timeBandDays, timeBandWindow } from './helpers/spaTimeBands';
import { countSpaGuests } from '@features/spa/utils/countSpaGuests';
import { isInlineV2Step } from './helpers/v2Steps';
import { FormStep, Package, PackagesFormValues, PackagesSchema, PackageType } from './types';

type DynamicPackagesConfigProps = DynamicPackagesConfigurationWizardStoreProps &
  DynamicPackagesConfigurationWizardProps &
  DynamicPackagesConfigurationWizardDispatchProps;

const PrefetchWrapper = () => {
  usePrefetchMultiGuestAvailability();
  return null;
};

export const DynamicPackagesConfigurationWizard: FC<DynamicPackagesConfigProps> = ({
  property,
  journeyParams,
  rate,
  room,
  loggedGuest,
  basketGuests,
  reservedSpaTimeSlots,
  updateBasket,
  createBasket,
  basketDetails,
  language,
  onInlineStepChange,
}) => {
  //stay info
  const children = journeyParams.children;
  const spaGuestAmount = countSpaGuests(journeyParams);
  const roomId = room.id;
  const rateId = rate.id;
  const fromDate = formatDateISO(journeyParams.fromDate);
  const toDate = journeyParams.toDate
    ? formatDateISO(journeyParams.toDate)
    : formatDateISO(journeyParams.fromDate.clone().add(1, 'days'));
  const [isModalOpen, setIsModalOpen] = useState(true);
  const queryClient = useQueryClient();

  //get data for the package
  const packageData: Package | undefined = rate.package ?? undefined;
  const isFetching = false;
  const isError = !packageData;
  const mappedPackageData = {
    ...packageData,
    experiences: (packageData?.experiences || []).map((exp) => ({
      ...exp,
      dayNumberRange: exp.dayNumberRange || [exp.dayNumber],
    })),
  };

  const stayDuration =
    mappedPackageData && mappedPackageData.numberOfNights !== 1
      ? mappedPackageData.numberOfDays ||
        moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days')
      : moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days');

  // A package is "repeating" when it's designed for 1 night but the customer books multiple nights
  // experiences from day 0/1 repeat across the stay so experiences mapped to day 0 are available on all days excluding checkout
  // experiences mapped to day 1 are available on all days except day 0
  // A package is "flexible" when it has multiple nights with experiences mapped to specific days
  const packageType =
    mappedPackageData?.numberOfNights === 1 ? PackageType.REPEATING : PackageType.FLEXIBLE;
  const isRepeatedFlexibleEnabled = useFeatureIsOn('feature.package-repeated-flexible');
  const isEcom2Enabled = useFeatureIsOn('feature.ecom2');
  const isMultiRoom = some(journeyParams.rooms) && journeyParams.rooms.length > 1;

  const { data: availableExtras, isLoading: isLoadingRoomExtras } = useRoomExtras({
    ...journeyParams,
    fromDate,
    toDate,
    roomId,
    rateId,
    language,
  });

  const { data: spaDay } = useGetSpaPackage(
    property.id,
    Number(mappedPackageData?.meta?.entityId),
    language,
    true
  );

  const { data: timeslots } = useGetSpaPackageTimeslots(
    property.id,
    Number(mappedPackageData?.meta?.entityId),
    formatDateISO(moment(journeyParams.fromDate)),
    spaGuestAmount,
    timeBandDays(packageType, stayDuration)
  );

  const { data: spaExtras = [], isLoading: isLoadingSpaExtras } = useGetSpaPackageExtras(
    property.id,
    Number(mappedPackageData?.meta?.entityId),
    language,
    formatDateISO(moment(journeyParams.fromDate)),
    true
  );

  const timeslot = timeBandWindow(timeslots);
  const datesArray = formatDateRangeISO(journeyParams.fromDate, stayDuration);
  datesArray.forEach((date) => {
    useGetSpaPackageConfigGroups(
      spaDay?.configurationGroups,
      property.id,
      Number(mappedPackageData?.meta?.entityId),
      date,
      spaGuestAmount,
      timeslot?.startTime,
      timeslot?.endTime
    );

    useGetSpaPackageConfigGroups(
      spaDay?.configurationGroups,
      property.id,
      Number(mappedPackageData?.meta?.entityId),
      date,
      1,
      timeslot?.startTime,
      timeslot?.endTime
    );
  });

  //route handling
  const router = useRouter();
  useEffect(() => {
    if (isError) {
      router.push({
        pathname: getBookingRoute(router.pathname),
        query: { id: rateId, bookingStep: 'guestDetailsStep', room: roomId },
      });
    }
  }, [isError]);

  //form management
  const form = useForm<PackagesFormValues>({
    resolver: zodResolver(PackagesSchema),
  });
  const { setValue, watch } = form;
  const { snapshot, isRestoring, clearSnapshot } = useRestoredLanguageChangeSnapshot();

  //form submission
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showConfirmation = () => {
    setIsModalOpen(false);
    setIsSubmitting(false);
    setIsConfirmationVisible(true);
    useInvalidatePackagesAvailability(queryClient);
    useInvalidateSpaPackageConfigGroups(queryClient);
  };
  const hideConfirmation = () => {
    router.push({
      pathname: getDetailRoute(router.pathname),
      query: {
        id: rate.id,
      },
    });
    setIsConfirmationVisible(false);
  };

  const bookingDetails = watch();
  //step management
  const { steps: experienceSteps, includedExperiences } = getExperienceSteps(
    mappedPackageData?.experiences || [],
    stayDuration,
    packageType
  );

  const selectedExperiences = watch('selectedExperiences');

  useEffect(() => {
    if (includedExperiences && !selectedExperiences) {
      setValue('selectedExperiences', includedExperiences);
    }
  }, [includedExperiences, selectedExperiences]);

  const allFormSteps: FormStep[] = [
    {
      step: 'guestDetailsStep',
      type: 'rooms',
    },
    ...(some(availableExtras) ? [{ step: 'roomExtrasStep', type: 'rooms' }] : []),
    ...(isEcom2Enabled ? foldSelectionIntoExperienceStep(experienceSteps) : experienceSteps),
    ...(some(spaExtras) ? [{ step: 'spaExtrasStep', type: 'spa' }] : []),
    ...getTablesSteps(mappedPackageData?.restaurants || [], packageType, stayDuration),
  ];
  const {
    stepNavigatorComponent,
    goToNextStep,
    goToPreviousStep,
    isLastStep,
    currentFormStep,
    currentStepIndex,
  } = useStepNavigation(rate.id || '', packageData, allFormSteps, {
    step: snapshot?.step,
    isReady: !isLoadingRoomExtras && !isLoadingSpaExtras,
    onRestored: () => {
      if (snapshot) form.reset(snapshot.values);
      clearSnapshot();
    },
  });

  useSaveSnapshotOnLanguageChange({
    pendingSnapshot: snapshot,
    isPackageAddingOrAdded: isSubmitting || isConfirmationVisible,
    getSnapshot: () => ({ step: currentFormStep.step, values: form.getValues() }),
  });

  const experienceDayOffsetsByGroupId = Object.fromEntries(
    (mappedPackageData?.experiences || [])
      .filter((experience) => experience.meta?.entityId !== undefined)
      .map((experience) => [String(experience.meta?.entityId), experience.dayNumber || 0])
  );

  const handleAddToBasket = async () => {
    setIsSubmitting(true);
    try {
      await addToBasket({
        createBasket,
        updateBasket,
        bookingDetails,
        roomRateEntity: rate,
        roomId,
        children,
        childAges: journeyParams.childAges,
        fromDate,
        toDate,
        property,
        language,
        basketDetails,
        showConfirmation,
        spaPackageId: mappedPackageData?.meta?.entityId || '',
        spaOpenTime: timeslot?.startTime || '',
        spaCloseTime: timeslot?.endTime || '',
        spaTimeBands: timeslots,
        maxGuestsPerExperience: spaDay?.numberOfGuests || spaGuestAmount,
        availableExtras: availableExtras || [],
        loggedInUserGuestData: mapLoggedInUserToGuest(loggedGuest),
        packageType: packageType,
        promoRateCode: journeyParams.promoRateCode,
        isRepeatedFlexibleEnabled,
        stayDuration,
        experienceDayOffsetsByGroupId,
      });
    } catch {
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const heldSpaTimes = useMemo(
    () => heldSpaTimesFromBasket(basketDetails?.items),
    [basketDetails?.items]
  );

  const packageConfigValue = {
    property,
    language,
    journeyParams,
    stayDuration,
    spaGuestAmount,
    packageType,
    spaPackageId: mappedPackageData?.meta?.entityId || '',
    spaOpenTime: timeslot?.startTime || '',
    spaCloseTime: timeslot?.endTime || '',
    spaTimeBands: timeslots,
    rate,
    room,
    availableExtras,
    basketGuests: basketGuests || [],
    loggedInGuest: loggedGuest,
    reservedSpaTimeSlots: reservedSpaTimeSlots || [],
    heldSpaTimes,
    isRepeatedFlexibleEnabled,
    isEcom2Enabled,
  };

  const isInlineStep = isInlineV2Step(currentFormStep.step, isEcom2Enabled, isMultiRoom);
  const isWizardVisible = isSubmitting || (isModalOpen && !isError);
  const showsInlineStep = isInlineStep && isWizardVisible;

  useEffect(() => {
    onInlineStepChange?.(showsInlineStep);
    return () => onInlineStepChange?.(false);
  }, [showsInlineStep, onInlineStepChange]);

  const wizardNavigationValue = {
    currentFormStep,
    allFormSteps,
    handleNextStep: () => {
      if (!isLastStep) {
        goToNextStep();
      } else {
        handleAddToBasket();
      }
    },
    handlePreviousStep: goToPreviousStep,
    currentStepIndex,
    isLastStep,
    stickyBarId: STICKY_BAR_ID,
  };

  const formStepsProps = {
    form,
    isSubmitting,
    addToBasket: handleAddToBasket,
  };

  //basket drawer
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;
  const showBasketDrawer = isConfirmationVisible && basketDetails;

  const modules = Object.keys(property.modulesExcludingDisabled);

  const wizardSteps = (
    <PackageConfigurationProvider value={packageConfigValue}>
      <WizardNavigationProvider value={wizardNavigationValue}>
        <FormProvider {...form}>
          <PrefetchWrapper />
          {!isRestoring && <FormSteps key={snapshot ? 'restoring' : 'ready'} {...formStepsProps} />}
        </FormProvider>
      </WizardNavigationProvider>
    </PackageConfigurationProvider>
  );

  return (
    <div>
      <Spinner showDelayMessage isActive={isRestoring} />
      {showBasketDrawer && (
        <BasketDrawerVersion
          open={isConfirmationVisible}
          onClose={hideConfirmation}
          propertyModules={modules}
          hideArrivalTimes={hideArrivalTimes}
        />
      )}
      {isInlineStep ? (
        showsInlineStep && wizardSteps
      ) : (
        <Modal isVisible={isWizardVisible}>
          <Spinner showDelayMessage isActive={isSubmitting || (isFetching && !mappedPackageData)} />
          <Header>{stepNavigatorComponent}</Header>
          <InnerScroller>
            <FormWrapper>{wizardSteps}</FormWrapper>
          </InnerScroller>
        </Modal>
      )}
      {!isInlineStep && <StickyBarWrapper id={STICKY_BAR_ID} zIndex={9999} />}
    </div>
  );
};
